import { FC, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { $ModalStore, closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { setLastOpenedWorkspaceId } from '@nx-frontend/shared/api';
import { clientGetWorkspace } from '@nx-frontend/shared/api/webnotes/client';
import { useRouter } from '@nx-frontend/shared/hooks/router-proxy';
import { setWorkspacesEvent } from '@nx-frontend/shared/store/current-workspace';
import { setActiveItemIdEvent } from '@nx-frontend/shared/store/feature-menu-tree';
import { reinitSearchEvent, searchValueChanged } from '@nx-frontend/shared/store/search';
import { $user } from '@nx-frontend/shared/store/user';
import { updateWorkspaceAccessEvent } from '@nx-frontend/shared/store/workspace-access';
import { $workspaces } from '@nx-frontend/shared/store/workspaces';
import { MenuType, PopupPayload, PopupType, SuccessMoveCopyPayload } from '@nx-frontend/shared/types';

const getSuccessMoveCopyPayload = (payload: PopupPayload | undefined): SuccessMoveCopyPayload | null => {
  if (payload && payload.payloadType === PopupType.SUCCESS_MOVE_COPY) {
    return payload;
  }
  return null;
};

export const SuccessMoveCopyPopup: FC = () => {
  const [
    store,
    closeModal,
    workspaces,
    setSearchValue,
    reinitSearch,
    user,
    setCurrentWorkspace,
    updateWorkspaceAccess,
    setActiveItemId,
  ] = useUnit([
    $ModalStore,
    closeModalEvent,
    $workspaces,
    searchValueChanged,
    reinitSearchEvent,
    $user,
    setWorkspacesEvent,
    updateWorkspaceAccessEvent,
    setActiveItemIdEvent,
  ]);
  const router = useRouter();
  const { toast } = useToast();
  const t = useTranslations('webnotes');

  const modal = store.find((modal) => modal.payload && modal.payload.payloadType === PopupType.SUCCESS_MOVE_COPY);
  const payload = getSuccessMoveCopyPayload(modal?.payload as SuccessMoveCopyPayload);
  const { workspaceId, noteId } = payload || {};

  const onCloseClick = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const onGoToPageClick = useCallback(async () => {
    closeModal();
    const selectedWorkspace = workspaces.find((workspace) => workspace.globalId === workspaceId);
    // если воркспейс на который переходим, принадлежит текущей орге, вызываем через роутер
    if (selectedWorkspace) {
      setCurrentWorkspace(selectedWorkspace);
      if (workspaceId) {
        updateWorkspaceAccess({ workspaceId });
      }
      setSearchValue('');
      reinitSearch();

      if (user.id && selectedWorkspace && workspaces) {
        setLastOpenedWorkspaceId({
          userId: user.id,
          orgId: selectedWorkspace.orgId,
          workspaceId: selectedWorkspace.globalId,
        });
      }
      setActiveItemId({ id: `note#${noteId}`, menuType: MenuType.WEBNOTES_MENU });
      router.push(`/space/${workspaceId}/page/${noteId}`);
    } else {
      const workspace = await clientGetWorkspace(workspaceId);
      if (workspace && workspace.orgId) {
        window.location.href = `/auth/open-org/${workspace.orgId}?success=/space/${workspaceId}/page/${noteId}`;
      } else {
        toast({ title: t('webnotes-couldnt_navigate_to_page') });
      }
    }
  }, [
    closeModal,
    workspaces,
    workspaceId,
    setCurrentWorkspace,
    setSearchValue,
    reinitSearch,
    user.id,
    setActiveItemId,
    noteId,
    router,
    updateWorkspaceAccess,
    toast,
    t,
  ]);

  return (
    <div
      data-cy="success-move-copy-popup"
      className="flex p-[0] items-center rounded gap-4 mt-2 min-w-[480px] justify-end"
    >
      <Button size={ButtonSize.XL} type={ButtonType.STROKED} onClick={onCloseClick}>
        {t('webnotes-close')}
      </Button>
      <Button size={ButtonSize.XL} type={ButtonType.FLAT} onClick={onGoToPageClick}>
        {t('webnotes-go_to_page')}
      </Button>
    </div>
  );
};
